import { useState, useRef, useCallback, useEffect } from 'react';
import { useToastStore } from '../store/toastStore';

export interface VoiceChatState {
  isConnected: boolean;
  isRecording: boolean;
  isProcessing: boolean;
  error: string | null;
  hasMicrophonePermission: boolean;
  transcript: string | null;
}

const MICROPHONE_PERMISSION_KEY = 'microphone_permission_granted';

const isIOS = typeof navigator !== 'undefined' && (
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
);

// Глобальный контекст для Safari, чтобы не пересоздавать его
let sharedAudioCtx: AudioContext | null = null;

interface UseVoiceChatOptions {
  onTranscript?: (text: string) => void;
}

export function useVoiceChat(userId?: number, options?: UseVoiceChatOptions) {
  const [state, setState] = useState<VoiceChatState>({
    isConnected: true,
    isRecording: false,
    isProcessing: false,
    error: null,
    hasMicrophonePermission: false,
    transcript: null
  });

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  const sendToSTT = useCallback(async (audioBlob: Blob): Promise<string> => {
    const formData = new FormData();
    const ext = audioBlob.type.includes('mp4') ? 'm4a' : 'webm';
    formData.append('audio', audioBlob, `recording.${ext}`);
    const response = await fetch('/api/stt', { method: 'POST', body: formData });
    const result = await response.json();
    if (!result.success) throw new Error(result.error || 'STT failed');
    return result.text || '';
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setState(prev => ({ ...prev, isRecording: false }));
  }, []);

  const startRecording = useCallback(async () => {
    // --- ШАГ 0: СИНХРОННАЯ АКТИВАЦИЯ (Критично для Safari PWA) ---
    // Это должно быть ПЕРВЫМ действием в функции, без await перед этим.
    if (!sharedAudioCtx) {
      const AudioContextClass = (window.AudioContext || (window as any).webkitAudioContext);
      sharedAudioCtx = new AudioContextClass();
    }

    // Пытаемся запустить контекст. В Safari PWA это "пробивает" блокировку.
    const resumePromise = sharedAudioCtx.state === 'suspended' ? sharedAudioCtx.resume() : Promise.resolve();

    try {
      // Очистка старых потоков
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
      audioChunksRef.current = [];

      // Ждем активации контекста
      await resumePromise;

      // Запрос микрофона
      const constraints = {
        audio: isIOS ? {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        } : true
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      // Проверяем, не успел ли Safari убить трек сразу после выдачи (Capture Failure)
      const track = stream.getAudioTracks()[0];
      if (!track || track.readyState === 'ended') {
        throw new Error('Capture failure: track ended immediately');
      }

      streamRef.current = stream;

      const mimeType = isIOS && MediaRecorder.isTypeSupported('audio/mp4')
        ? 'audio/mp4'
        : MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : '';

      const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(t => t.stop());
          streamRef.current = null;
        }

        const audioBlob = new Blob(audioChunksRef.current, { type: mediaRecorder.mimeType });
        if (audioBlob.size < 500) {
          setState(prev => ({ ...prev, isProcessing: false, isRecording: false }));
          return;
        }

        setState(prev => ({ ...prev, isProcessing: true }));
        try {
          const text = await sendToSTT(audioBlob);
          setState(prev => ({ ...prev, isProcessing: false, transcript: text, error: null }));
          if (options?.onTranscript) options.onTranscript(text);
        } catch (error: any) {
          setState(prev => ({ ...prev, isProcessing: false, error: error.message }));
        }
      };

      // Запуск
      mediaRecorder.start(250);

      setState(prev => ({
        ...prev,
        isRecording: true,
        error: null,
        transcript: null,
        hasMicrophonePermission: true
      }));
      localStorage.setItem(MICROPHONE_PERMISSION_KEY, 'true');

    } catch (err) {
      console.error('Recording error:', err);
      // Если поймали Capture Failure, сбрасываем всё, чтобы юзер мог нажать еще раз
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
      setState(prev => ({ ...prev, isRecording: false, error: 'Микрофон не активирован. Нажмите еще раз.' }));
      useToastStore.getState().show('Микрофон временно недоступен. Попробуйте нажать еще раз.', 'error');
    }
  }, [sendToSTT, options, stopRecording]);

  useEffect(() => {
    const savedPermission = localStorage.getItem(MICROPHONE_PERMISSION_KEY) === 'true';
    if (savedPermission) setState(prev => ({ ...prev, hasMicrophonePermission: true }));
  }, []);

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current?.state !== 'inactive') mediaRecorderRef.current?.stop();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const connect = useCallback(() => setState(prev => ({ ...prev, isConnected: true })), []);
  const disconnect = useCallback(() => {
    stopRecording();
    setState(prev => ({ ...prev, isConnected: false, isRecording: false }));
  }, [stopRecording]);

  return {
    ...state,
    connect,
    disconnect,
    startRecording,
    stopRecording,
    isSpeaking: state.isProcessing
  };
}
